import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useAtomValue } from 'jotai'
import { useTranslate, authenticationState } from '@repo/unicity'
import { ActionButton, ErrorMessages, CheckoutBreadcrumb, type BreadcrumbStep } from '@/core/components'
import CheckoutLayoutSkeleton from '@/core/checkout/components/checkout-layout-skeleton.component'
import { cartAtom } from '@/core/cart/cart.store'
import { useTaiwanPayment } from '../../hooks/useTaiwanPayment'
import { useTaiwanCartSync } from '../../hooks/useTaiwanCartSync'
import TaiwanOrderSummary from '../checkout/components/TaiwanOrderSummary'
import TaiwanShipToSection from './components/TaiwanShipToSection'
import TaiwanPaymentMethods from './components/TaiwanPaymentMethods'

// Figma 689:5733 / 689:6280 (desktop) + 2540:147412 (mobile) — white card,
// radius 12, no border. Padding 20 on mobile, 24 from the desktop breakpoint.
const SectionCard = styled(Box)`
    border-radius: 12px;
    padding: 20px;
    background: #ffffff;

    @media (min-width: 1024px) {
        padding: 24px;
    }
`

// Figma 689:6430 — content row: form 760 + summary 368, gap 24, max 1152.
const LayoutGrid = styled(Box)`
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    max-width: 1152px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
    padding: 24px 20px;
    @media (min-width: 1024px) {
        grid-template-columns: minmax(0, 1fr) 368px;
        align-items: start;
    }
`

// Taiwan 4-step flow — passed to the skeleton so its stepper matches the loaded
// one (avoids a 3↔4 jump while the payment data loads).
const TAIWAN_SKELETON_STEPS = [
    { key: 'customise', label: 'customise' },
    { key: 'shipping', label: 'shipping' },
    { key: 'billing', label: 'billing' },
    { key: 'purchase', label: 'purchase' }
]

// Breadcrumb targets. Step 1 (Shipping) resolves to the auth-specific route so
// the URL stays correct on back-nav: /checkout/shipping for members,
// /checkout/create-account for guests (not the generic /checkout). Carries the
// checkout uuid on back-navigation for every step except Customise.
const buildStepPath = (index: number, uuid: string | null, isAuthenticated: boolean): string => {
    const paths = ['/products', isAuthenticated ? '/checkout/shipping' : '/checkout/create-account', '/checkout/payment', '/checkout/review']
    const path = paths[index] ?? '/'
    return index !== 0 && uuid ? `${path}?uuid=${uuid}` : path
}

export default function TaiwanPaymentsModule() {
    const { translate } = useTranslate()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const auth = useAtomValue(authenticationState)
    const isAuthenticated = !!(auth.token && auth.baId)
    const { initialized, loadError, errors } = useTaiwanPayment()
    const cart = useAtomValue(cartAtom)
    const { flush } = useTaiwanCartSync()
    const [submitting, setSubmitting] = useState(false)

    // Final step → re-update the format (flush any pending cart edit: PUT + refetch)
    // so the review page shows the authoritative recalculated order.
    const goToReview = async () => {
        const uuid = searchParams.get('uuid')
        setSubmitting(true)
        try {
            await flush(JSON.stringify(cart))
        } finally {
            setSubmitting(false)
        }
        navigate(`/checkout/review${uuid ? `?uuid=${uuid}` : ''}`)
    }

    const steps: BreadcrumbStep[] = useMemo(
        () => [
            { key: 'customise', label: translate('customise') },
            { key: 'shipping', label: translate('shipping') },
            { key: 'billing', label: translate('billing') },
            { key: 'purchase', label: translate('purchase') }
        ],
        [translate]
    )

    if (!initialized) {
        return <CheckoutLayoutSkeleton steps={TAIWAN_SKELETON_STEPS} activeIndex={2} data-testid="taiwan-payment-skeleton" />
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CheckoutBreadcrumb
                steps={steps}
                activeIndex={2}
                onStepClick={index => navigate(buildStepPath(index, searchParams.get('uuid'), isAuthenticated))}
                sx={{ pt: '40px' }}
                data-testid="taiwan-payment-breadcrumb"
            />

            <LayoutGrid>
                <Stack spacing={3} data-testid="taiwan-payment-left">
                    <SectionCard data-testid="taiwan-payment-section">
                        <TaiwanShipToSection />
                    </SectionCard>

                    <SectionCard data-testid="taiwan-payment-methods-section">
                        <TaiwanPaymentMethods />

                        <ErrorMessages list={loadError.length > 0 ? loadError : errors} sx={{ mt: 2 }} data-testid="taiwan-payment-errors" />

                        <Stack spacing={2} alignItems="center" sx={{ mt: 4, width: 280, mx: 'auto' }} data-testid="taiwan-payment-submit-box">
                            <ActionButton
                                text={submitting ? '' : translate('next_final_step')}
                                onClick={goToReview}
                                disabled={submitting || loadError.length > 0}
                                startIcon={submitting ? <CircularProgress size={22} color="inherit" /> : undefined}
                                data-testid="taiwan-payment-submit"
                            />
                            <Typography
                                component="a"
                                href="#"
                                sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 400, color: '#003b6f', textDecoration: 'underline', textAlign: 'center' }}
                                data-testid="taiwan-payment-money-back">
                                {translate('our_money_back_guarantee')}
                            </Typography>
                        </Stack>
                    </SectionCard>
                </Stack>

                <Box sx={{ order: -1, '@media (min-width:1024px)': { order: 0 } }} data-testid="taiwan-payment-order-summary-box">
                    <TaiwanOrderSummary data-testid="taiwan-payment-order-summary" />
                </Box>
            </LayoutGrid>
        </Box>
    )
}
